if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', class extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.list = this.querySelector('[data-recently-viewed-list]');
      this.empty = this.querySelector('[data-recently-viewed-empty]');
      this.clearButton = this.querySelector('[data-recently-viewed-clear]');
      this.placeholderTemplate = this.querySelector('[data-recently-viewed-placeholder]');
      this.displayLimit = Math.max(1, Math.min(12, Number(this.dataset.displayLimit) || 8));
      this.storageLimit = 24;

      this.addEventListener('click', event => this.onClick(event), { signal: this.abortController.signal });
      this.storeCurrentProduct();
      this.render();
    }

    get storageKey() {
      return `gastrolux:recently-viewed:${this.dataset.storageKey || 'store'}`;
    }

    read() {
      try {
        const value = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
        return Array.isArray(value) ? value.filter(item => this.validRecord(item)) : [];
      } catch {
        return [];
      }
    }

    write(items) {
      try { localStorage.setItem(this.storageKey, JSON.stringify(items.slice(0, this.storageLimit))); }
      catch { return; }
      this.updateCount(items.length);
    }

    validRecord(item) {
      return Boolean(item && Number(item.productId) > 0 && Number(item.variantId) > 0 && typeof item.title === 'string' && typeof item.url === 'string');
    }

    storeCurrentProduct() {
      const dataNode = document.querySelector('[data-recently-viewed-current-product]');
      if (!dataNode) return;
      let current;
      try { current = JSON.parse(dataNode.textContent); }
      catch { return; }
      if (!this.validRecord(current)) return;

      current.viewedAt = Date.now();
      const items = this.read().filter(item => Number(item.productId) !== Number(current.productId));
      items.unshift(current);
      this.write(items);
    }

    onClick(event) {
      const clear = event.target.closest('[data-recently-viewed-clear]');
      if (clear) {
        event.preventDefault();
        this.write([]);
        this.render();
        return;
      }

      const remove = event.target.closest('[data-recently-viewed-remove]');
      if (!remove) return;
      event.preventDefault();
      const productId = Number(remove.dataset.productId);
      this.write(this.read().filter(item => Number(item.productId) !== productId));
      this.render();
    }

    refresh() {
      this.render();
    }

    render() {
      if (!this.list || !this.empty) return;
      const allItems = this.read();
      const items = allItems.slice(0, this.displayLimit);
      this.list.replaceChildren(...items.map(item => this.createCard(item)));
      this.list.hidden = items.length === 0;
      this.empty.hidden = items.length > 0;
      if (this.clearButton) this.clearButton.hidden = items.length === 0;
      this.updateCount(allItems.length);
    }

    createCard(item) {
      const article = document.createElement('article');
      article.className = 'recently-viewed-card';

      const mediaLink = document.createElement('a');
      mediaLink.className = 'recently-viewed-card__media';
      mediaLink.href = this.safeProductUrl(item.url);
      mediaLink.setAttribute('aria-label', item.title);
      const imageUrl = item.image ? this.safeImageUrl(item.image) : '';
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = item.imageAlt || item.title;
        image.width = 160;
        image.height = 200;
        image.loading = 'lazy';
        mediaLink.append(image);
      } else if (this.placeholderTemplate) {
        mediaLink.append(this.placeholderTemplate.content.cloneNode(true));
      }

      const details = document.createElement('div');
      details.className = 'recently-viewed-card__details';
      if (item.vendor) {
        const vendor = document.createElement('p');
        vendor.className = 'recently-viewed-card__vendor';
        vendor.textContent = item.vendor;
        details.append(vendor);
      }

      const title = document.createElement('a');
      title.className = 'recently-viewed-card__title';
      title.href = this.safeProductUrl(item.url);
      title.textContent = item.title;
      details.append(title);

      const price = document.createElement('p');
      price.className = 'recently-viewed-card__price';
      if (item.compareAtPrice) {
        const compare = document.createElement('s');
        compare.textContent = item.compareAtPrice;
        price.append(compare);
      }
      const current = document.createElement('span');
      current.textContent = item.price || '';
      price.append(current);
      details.append(price);

      const actions = document.createElement('div');
      actions.className = 'recently-viewed-card__actions';
      if (!item.available) {
        const soldOut = document.createElement('span');
        soldOut.className = 'recently-viewed-card__status';
        soldOut.textContent = this.dataset.soldOutLabel;
        actions.append(soldOut);
      } else if (item.directAdd) {
        const add = document.createElement('button');
        add.className = 'button-link button-link--compact';
        add.type = 'button';
        add.dataset.recentlyViewedAdd = '';
        add.dataset.variantId = String(item.variantId);
        add.dataset.quantity = String(Math.max(1, Number(item.quantity) || 1));
        add.setAttribute('aria-label', `${this.dataset.addLabel} ${item.title}`);
        add.textContent = this.dataset.addLabel;
        actions.append(add);
      } else {
        const choose = document.createElement('a');
        choose.className = 'button-link button-link--compact';
        choose.href = this.safeProductUrl(item.url);
        choose.textContent = this.dataset.optionsLabel;
        actions.append(choose);
      }

      const remove = document.createElement('button');
      remove.className = 'recently-viewed-card__remove';
      remove.type = 'button';
      remove.dataset.recentlyViewedRemove = '';
      remove.dataset.productId = String(item.productId);
      remove.setAttribute('aria-label', `${this.dataset.removeLabel} ${item.title}`);
      remove.textContent = this.dataset.removeLabel;
      actions.append(remove);
      details.append(actions);

      article.append(mediaLink, details);
      return article;
    }

    safeProductUrl(value) {
      try {
        const url = new URL(value, window.location.origin);
        if (url.origin !== window.location.origin) return window.Shopify?.routes?.root || '/';
        return `${url.pathname}${url.search}${url.hash}`;
      } catch {
        return window.Shopify?.routes?.root || '/';
      }
    }

    safeImageUrl(value) {
      try {
        const url = new URL(value, window.location.origin);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
      } catch {
        return '';
      }
    }

    updateCount(count) {
      document.querySelectorAll('[data-recently-viewed-count]').forEach(element => {
        element.textContent = `[${count}]`;
        element.hidden = false;
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }
  });
}
